import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import Loader from 'react-loader-spinner';
import { FiSave } from 'react-icons/fi';
import { API_URLS } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { authorizedPost, fileToBase64, getApiErrorMessage } from '../utilities/stadiumsApi';
import { useChampionships } from '../utilities/championships';
import { useAdminAccess } from '../utilities/adminAccess';
import { extractEntity, extractList, withCacheBust } from '../utilities/tournamentsApi';
import AdminPhotoUpload from '../components/AdminPhotoUpload';
import CustomModal from '../components/CustomModal';
import StadiumCrumbs from '../components/StadiumCrumbs';
import '../styles/createMatchModal.css';
import '../styles/stadiumsManagement.css';
import '../styles/tournamentsManagement.css';

const emptyForm = {
  name: '',
  family: '',
  father_name: '',
  position: '',
  socialVk: '',
  socialTel: '',
  socialTg: '',
};

export default function ManageCurator() {
  const { countryId, leagueId, curatorId } = useParams();
  const isNew = !curatorId;
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useI18n();
  const { champId, loading: champsLoading } = useChampionships();
  const { canWrite } = useAdminAccess('tournaments');

  const [form, setForm] = useState(emptyForm);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [photoError, setPhotoError] = useState('');
  const [errors, setErrors] = useState({});
  const [leagueName, setLeagueName] = useState('');
  const [countryName, setCountryName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteVisible, setDeleteVisible] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    if (!champId && champsLoading) return;
    initPage();
  }, [countryId, leagueId, curatorId, champId, champsLoading]);

  const goBackToLeague = () => {
    history.push(`/tournaments/${countryId}/league/${leagueId}`);
  };

  const initPage = async () => {
    setMessage({ text: '', type: '' });
    setErrors({});
    setPhotoFile(null);
    setPhotoError('');

    setLoading(true);
    try {
      if (champId) {
        const countriesResponse = await authorizedPost(dispatch, API_URLS.getAdminCountries, { champ_id: champId });
        const countries = extractList(countriesResponse.data);
        const country = countries.find((item) => Number(item.id) === Number(countryId));
        setCountryName(country?.name || '');
      }

      const leagueResponse = await authorizedPost(dispatch, API_URLS.getAdminLeague, { id: Number(leagueId) });
      const league = extractEntity(leagueResponse.data);
      setLeagueName(league.name || '');

      if (isNew) {
        setForm(emptyForm);
        setPhotoPreview('');
        return;
      }

      const curator = extractList(league.curators).find((item) => Number(item.id) === Number(curatorId));
      if (!curator) {
        setMessage({ text: t('trContactNotFound'), type: 'error' });
        setForm(emptyForm);
        setPhotoPreview('');
        return;
      }

      setForm({
        name: curator.name || '',
        family: curator.family || '',
        father_name: curator.father_name || '',
        position: curator.position || '',
        socialVk: curator.socialVk || '',
        socialTel: curator.socialTel || '',
        socialTg: curator.socialTg || '',
      });
      setPhotoPreview(curator.image ? withCacheBust(curator.image) : '');
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('stLoadError')), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: '' }));
    }
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = t('trFirstNameRequired');
    if (!form.family.trim()) nextErrors.family = t('trLastNameRequired');
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    setMessage({ text: '', type: '' });
    try {
      const payload = {
        name: form.name.trim(),
        family: form.family.trim(),
        father_name: form.father_name.trim() || null,
        position: form.position.trim() || null,
        socialVk: form.socialVk.trim(),
        socialTel: form.socialTel.trim(),
        socialTg: form.socialTg.trim(),
      };
      if (photoFile) {
        payload.image = await fileToBase64(photoFile);
      }

      if (isNew) {
        await authorizedPost(dispatch, API_URLS.addCurator, {
          ...payload,
          league_id: Number(leagueId),
        });
        setMessage({ text: t('trContactCreated'), type: 'success' });
        goBackToLeague();
      } else {
        await authorizedPost(dispatch, API_URLS.changeCurator, {
          ...payload,
          id: Number(curatorId),
        });
        setPhotoFile(null);
        setMessage({ text: t('stSaved'), type: 'success' });
      }
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('stSaveError')), type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await authorizedPost(dispatch, API_URLS.deleteCurator, { id: Number(curatorId) });
      goBackToLeague();
    } catch (error) {
      setDeleteVisible(false);
      setMessage({ text: getApiErrorMessage(error, t('stdDeleteError')), type: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const crumbs = [
    { label: t('navTournaments'), to: '/tournaments' },
    { label: countryName || t('trCountry'), to: `/tournaments/${countryId}` },
    { label: leagueName || t('trEditLeague'), to: `/tournaments/${countryId}/league/${leagueId}` },
    { label: isNew ? t('trNewContact') : ([form.name, form.family].filter(Boolean).join(' ') || t('trEditContact')) },
  ];

  if (loading) {
    return (
      <div className="layoutWrapper st-layout">
        <div className="matchCenterWrapper st-page">
          <div className="matchCenterHeader">
            <div className="st-header">
              <StadiumCrumbs items={crumbs} />
            </div>
          </div>
          <div className="st-loading">
            <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
            <p>{t('trLoading')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="layoutWrapper st-layout">
      <div className="matchCenterWrapper st-page tr-curator-page">
        <div className="matchCenterHeader">
          <div className="st-header">
            <StadiumCrumbs items={crumbs} />
          </div>
        </div>

        <div className="st-scroll">
          <div className="st-content">
            {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}

            <div className="st-block tr-curator-form">
              <div className="tr-curator-main">
                <div className="st-field tr-curator-photo">
                  <label>{t('stPhoto')}</label>
                  <AdminPhotoUpload
                    preview={photoPreview}
                    error={photoError}
                    disabled={saving}
                    onFileChange={(file, error, preview) => {
                      if (error) {
                        setPhotoError(error);
                        return;
                      }
                      setPhotoError('');
                      setPhotoFile(file);
                      setPhotoPreview(preview);
                    }}
                  />
                </div>

                <div className="st-form-grid tr-curator-fields">
                  <div className="st-field">
                    <label>{t('trLastName')} <span className="st-required">*</span></label>
                    <input
                      className={`st-input ${errors.family ? 'error' : ''}`}
                      value={form.family}
                      onChange={(event) => updateField('family', event.target.value)}
                    />
                    {errors.family ? <span className="st-field-error">{errors.family}</span> : null}
                  </div>

                  <div className="st-field">
                    <label>{t('trFirstName')} <span className="st-required">*</span></label>
                    <input
                      className={`st-input ${errors.name ? 'error' : ''}`}
                      value={form.name}
                      onChange={(event) => updateField('name', event.target.value)}
                    />
                    {errors.name ? <span className="st-field-error">{errors.name}</span> : null}
                  </div>

                  <div className="st-field">
                    <label>{t('trFatherName')}</label>
                    <input
                      className="st-input"
                      value={form.father_name}
                      onChange={(event) => updateField('father_name', event.target.value)}
                    />
                  </div>

                  <div className="st-field">
                    <label>{t('trPosition')}</label>
                    <input
                      className="st-input"
                      value={form.position}
                      placeholder={t('trPositionPh')}
                      onChange={(event) => updateField('position', event.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="st-form-grid tr-curator-socials">
                <div className="st-field">
                  <label>{t('trVk')}</label>
                  <input
                    className="st-input"
                    value={form.socialVk}
                    placeholder={t('trVkPh')}
                    onChange={(event) => updateField('socialVk', event.target.value)}
                  />
                </div>

                <div className="st-field">
                  <label>{t('trTelegram')}</label>
                  <input
                    className="st-input"
                    value={form.socialTg}
                    placeholder={t('trTelegramPh')}
                    onChange={(event) => updateField('socialTg', event.target.value)}
                  />
                </div>

                <div className="st-field">
                  <label>{t('trPhone')}</label>
                  <input
                    className="st-input"
                    value={form.socialTel}
                    placeholder={t('trPhonePh')}
                    onChange={(event) => updateField('socialTel', event.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {canWrite ? (
        <div className={`st-actions ${!isNew ? 'st-actions-split' : ''}`}>
          {!isNew ? (
            <button
              type="button"
              className="st-btn st-btn-danger"
              disabled={saving || deleting}
              onClick={() => setDeleteVisible(true)}
            >
              {t('delete')}
            </button>
          ) : null}
          <button
            type="button"
            className="st-btn st-btn-primary"
            disabled={saving}
            onClick={handleSave}
          >
            <FiSave /> {saving ? t('trLoading') : (isNew ? t('trAddContact') : t('trChangeContact'))}
          </button>
        </div>
        ) : null}
      </div>

      <CustomModal
        styles={{ width: 420, height: 'auto', paddingLeft: 32, paddingRight: 32 }}
        visible={deleteVisible}
        closeModal={() => setDeleteVisible(false)}
      >
        <div className="st-delete-modal">
          <div className="customModalHeader">{t('trDeleteContactTitle')}</div>
          <p>{t('trDeleteContactText')}</p>
          <div className="st-delete-actions">
            <button type="button" className="btn-modal btn-secondary-modal" onClick={() => setDeleteVisible(false)}>
              {t('cancel')}
            </button>
            <button type="button" className="btn-modal btn-danger-modal" disabled={deleting} onClick={handleDelete}>
              {t('delete')}
            </button>
          </div>
        </div>
      </CustomModal>
    </div>
  );
}
